/* ============================================================
   gimbel-form-guard.js - 2026-10-11

   WHY: bots are submitting the footer newsletter form and the CAD
   Download modal with fake or stolen email addresses (dot-stuffed
   Gmail such as l.a.rr.yp.c.h.iem@gmail.com, plus real third-party
   addresses). Every CAD submit fires the transactional "Your requested
   CAD files" email (Klaviyo flow RbEKQh), so those emails land on
   people who never asked for them and get reported as spam.

   WHAT: one capture-phase submit listener, loaded sitewide from
   sections/footer.liquid. It runs BEFORE the CAD module in
   sections/product-technical.liquid and before the native Shopify
   newsletter/contact POST, and blocks a submit when:
     1) the email is an obvious fake (dot-stuffed Gmail, double dots)
     2) nobody physically typed, tapped or clicked in the form
        (script-driven submit). Blocks ONCE per page load, then lets
        the next press through, so a real person loses one click at most
     3) this browser is submitting too often, or with more than
        2 different email addresses in 24 hours

   RULES OF THE HOUSE (see the 2026-10-08 note in product-technical):
   a block NEVER fakes success. It always shows a visible message.
   Any internal error fails OPEN so a real customer is never stuck.

   LIMITS: this is browser-side only. It cannot see IP addresses and a
   bot that posts straight to Shopify or Klaviyo without loading the
   page skips it. Server-side layers: Shopify hCaptcha (Online Store >
   Preferences) and a Klaviyo flow filter on RbEKQh.

   TESTING NOTE: more than 2 different emails from one browser in a day
   is blocked on purpose. Test with a private/incognito window.

   2026-10-11b: (v2) HubSpot's non-HubSpot-form collector records every
   submit EVENT it sees, including ones a form's own honeypot then
   throws away, so trapped bots were still creating HubSpot contacts and
   "Form submitted" notifications. Changes:
     - now also covers the first-party newsletter section form
       (sections/newsletter.liquid, form id gimbel-nl-form-*)
     - hidden-field (honeypot) check moved in here, for every covered
       form. A trip clears the field, blocks the press and stops the
       event before HubSpot or the form's own handler sees it
     - a hidden trap field is injected into the Shopify
       newsletter/contact forms, which had none
   ============================================================ */
(function () {
  'use strict';
  if (window.__gimbelFormGuard) return;
  window.__gimbelFormGuard = true;

  var KEY = 'gimbel_fg_v1';
  var DAY = 86400000;
  var TEN_MIN = 600000;
  var MAX_EMAILS_PER_DAY = 2;
  var LIMITS = {
    cad: { tenMin: 6, day: 20 },     /* engineers pull several CAD files in a row */
    signup: { tenMin: 2, day: 4 }    /* newsletter + contact forms */
  };
  var HELP = ' Need help? Email sales@gimbelautomation.com.';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /* Hidden trap fields: the CAD modal's, the newsletter section's, and ours. */
  var HP_SEL = 'input[name="gimbel_cad_ref"], input[name="website"], input[name="gimbel_hp_url"]';

  function kindOf(form) {
    if (!form || !form.matches) return null;
    if (form.matches('.gimbel-cad-form')) return 'cad';
    if (form.id && form.id.indexOf('gimbel-nl-form-') === 0) return 'signup';
    if (form.querySelector('input[name="contact[email]"]')) return 'signup';
    return null;
  }

  /* True when a hidden trap field was filled. Clears it, so a real person
     whose autofill touched it gets through on the next press. */
  function trapTripped(form) {
    var hit = false;
    var fields = form.querySelectorAll(HP_SEL);
    for (var i = 0; i < fields.length; i++) {
      if (fields[i].value) { hit = true; fields[i].value = ''; }
    }
    return hit;
  }

  /* Shopify newsletter/contact forms ship with no trap field: add one. */
  function addTraps() {
    try {
      var inputs = document.querySelectorAll('form input[name="contact[email]"]');
      for (var i = 0; i < inputs.length; i++) {
        var form = inputs[i].form;
        if (!form || form.querySelector(HP_SEL)) continue;
        var box = document.createElement('div');
        box.setAttribute('aria-hidden', 'true');
        box.style.cssText = 'position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
        var f = document.createElement('input');
        f.type = 'text';
        f.name = 'gimbel_hp_url';
        f.tabIndex = -1;
        f.autocomplete = 'off';
        f.setAttribute('data-1p-ignore', '');
        f.setAttribute('data-lpignore', 'true');
        f.setAttribute('data-bwignore', '');
        f.setAttribute('data-form-type', 'other');
        box.appendChild(f);
        form.appendChild(box);
      }
    } catch (e) {}
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', addTraps);
  else addTraps();

  function emailOf(form) {
    var i = form.querySelector('input[type="email"], input[name="contact[email]"]');
    return i ? String(i.value || '').trim().toLowerCase() : '';
  }

  /* Obvious fakes only. first.middle.last@gmail.com (2 dots) is allowed. */
  function looksFake(email) {
    var at = email.lastIndexOf('@');
    var local = email.slice(0, at);
    var domain = email.slice(at + 1);
    if (local.indexOf('..') !== -1) return true;
    if (local.charAt(0) === '.' || local.charAt(local.length - 1) === '.') return true;
    var dots = local.split('.').length - 1;
    if (/^(gmail|googlemail)\.com$/.test(domain)) return dots >= 3;
    return dots >= 5;
  }

  function hash(s) {
    var h = 5381;
    for (var i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    return String(h);
  }

  /* Returns null when storage is unavailable (private mode etc.): limits are skipped. */
  function load() {
    try {
      var a = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      var now = Date.now();
      if (!Array.isArray(a)) return [];
      return a.filter(function (r) { return r && typeof r.t === 'number' && now - r.t < DAY; });
    } catch (e) { return null; }
  }

  function save(a) {
    try { window.localStorage.setItem(KEY, JSON.stringify(a.slice(-60))); } catch (e) {}
  }

  /* Forms a real person has physically interacted with. */
  var touched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var nudged = typeof WeakSet === 'function' ? new WeakSet() : null;
  ['keydown', 'pointerdown', 'mousedown', 'touchstart', 'paste', 'click'].forEach(function (type) {
    document.addEventListener(type, function (e) {
      if (!touched || !e.isTrusted) return;
      var f = e.target && e.target.closest && e.target.closest('form');
      if (f) touched.add(f);
    }, true);
  });

  function say(form, msg) {
    var el = form.querySelector('.gimbel-cad-status, .gimbel-nl-status');
    if (el) {
      el.className = (el.className.indexOf('gimbel-nl-status') !== -1 ? 'gimbel-nl-status' : 'gimbel-cad-status') + ' is-error';
      el.textContent = msg;
      return;
    }
    el = form.querySelector('.gimbel-guard-msg');
    if (!el) {
      el = document.createElement('p');
      el.className = 'gimbel-guard-msg';
      el.setAttribute('role', 'alert');
      el.style.cssText = 'margin:8px 0 0;padding:8px 10px;border-radius:6px;background:#fff;color:#b3261e;font-size:13px;line-height:1.4;text-align:left;';
      form.appendChild(el);
    }
    el.textContent = msg;
  }

  /* Returns a message when the submit must be blocked, otherwise null. */
  function check(form, kind, email) {
    /* Invalid or incomplete input: the form's own validation handles it. */
    if (!EMAIL_RE.test(email)) return null;
    if (kind === 'cad') {
      var consent = form.querySelector('input[name="cad_consent"]');
      if (consent && !consent.checked) return null;
    }

    if (looksFake(email)) {
      return 'Please use a different email address.' + HELP;
    }

    if (touched && nudged && !touched.has(form) && !nudged.has(form)) {
      nudged.add(form);
      return 'Almost there. Please press the button once more.';
    }

    var log = load();
    if (log) {
      var now = Date.now();
      var h = hash(email);
      var emails = {};
      var n10 = 0;
      var nDay = 0;
      log.forEach(function (r) {
        emails[r.e] = 1;
        if (r.k === kind) {
          nDay++;
          if (now - r.t < TEN_MIN) n10++;
        }
      });
      if (!emails[h] && Object.keys(emails).length >= MAX_EMAILS_PER_DAY) {
        return 'Too many different email addresses from this device today.' + HELP;
      }
      var lim = LIMITS[kind];
      if (n10 >= lim.tenMin || nDay >= lim.day) {
        return 'Too many requests in a short time. Please try again later.' + HELP;
      }
      log.push({ t: now, e: h, k: kind });
      save(log);
    }
    return null;
  }

  document.addEventListener('submit', function (e) {
    try {
      if (window.Shopify && window.Shopify.designMode) return;
      var form = e.target;
      var kind = kindOf(form);
      if (!kind) return;
      var msg = trapTripped(form)
        ? 'Almost there. Please press the button once more.'
        : check(form, kind, emailOf(form));
      if (msg) {
        e.preventDefault();
        e.stopImmediatePropagation();
        say(form, msg);
      }
    } catch (err) {
      /* Fail open: a guard bug must never block a real customer. */
    }
  }, true);
})();
